import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

/** Opção do menu de ações (⋮). */
export interface OpcaoMenuAcoes {
  readonly id: string;
  readonly rotulo: string;
  /** Ícone Feather do design system, ex.: "edit-2". */
  readonly icone: string;
  /** Fica separada das anteriores por uma linha (ações que mudam o registro na hora). */
  readonly separada?: boolean;
  readonly desabilitada?: boolean;
  /** Explicação exibida ao passar o cursor, ex.: por que a opção está desabilitada. */
  readonly dica?: string;
}

/** Posição do menu na tela (`position: fixed`, para não ser cortado por rolagem de tabela). */
interface PosicaoMenu {
  readonly top: number | null;
  readonly bottom: number | null;
  readonly right: number;
}

/** Altura aproximada do menu, usada para decidir se abre para cima. */
const ALTURA_MENU = 150;
const DISTANCIA_MENU = 4;

let proximoId = 0;

/**
 * Botão ⋮ (vivest-icon-button do design system) que abre um menu de ações da linha. O design
 * system não tem componente de menu, então a lista é `role="menu"` com os tokens dele.
 * Fecha ao escolher, clicar fora, rolar a página, redimensionar ou com Esc; setas, Home e End
 * percorrem as opções.
 *
 * @example
 * <app-menu-acoes rotulo="Ações de Resgate" [opcoes]="opcoes" (escolher)="aoEscolher($event)" />
 */
@Component({
  selector: 'app-menu-acoes',
  standalone: true,
  // Web components do design system Vivest (icon-button, icon).
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './menu-acoes.component.html',
  styleUrl: './menu-acoes.component.scss',
  host: {
    '(document:click)': 'fecharAoClicarFora($event)',
    '(window:resize)': 'fechar()',
    '(window:scroll)': 'fechar()',
  },
})
export class MenuAcoesComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  readonly opcoes = input.required<readonly OpcaoMenuAcoes[]>();
  /** Nome acessível do botão e do menu, ex.: "Ações de Resgate". */
  readonly rotulo = input.required<string>();
  readonly escolher = output<string>();

  readonly idMenu = `menu-acoes-${++proximoId}`;
  readonly aberto = signal(false);
  readonly posicao = signal<PosicaoMenu>({ top: null, bottom: null, right: 0 });

  alternar(event: Event): void {
    if (this.aberto()) {
      this.fechar(true);
      return;
    }
    const area = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const cabeAbaixo = area.bottom + DISTANCIA_MENU + ALTURA_MENU <= window.innerHeight;
    this.posicao.set({
      top: cabeAbaixo ? area.bottom + DISTANCIA_MENU : null,
      bottom: cabeAbaixo ? null : window.innerHeight - area.top + DISTANCIA_MENU,
      right: window.innerWidth - area.right,
    });
    this.aberto.set(true);
    afterNextRender(() => this.itens()[0]?.focus(), { injector: this.injector });
  }

  selecionar(opcao: OpcaoMenuAcoes): void {
    if (opcao.desabilitada) {
      return;
    }
    this.fechar(true);
    this.escolher.emit(opcao.id);
  }

  /** Fecha o menu; `devolverFoco` leva o foco de volta ao botão ⋮. */
  fechar(devolverFoco = false): void {
    if (!this.aberto()) {
      return;
    }
    this.aberto.set(false);
    if (devolverFoco) {
      this.gatilho()?.focus();
    }
  }

  navegar(event: KeyboardEvent): void {
    const itens = this.itens();
    const atual = itens.indexOf(document.activeElement as HTMLElement);
    const destinos: Record<string, number> = {
      ArrowDown: (atual + 1) % itens.length,
      ArrowUp: (atual - 1 + itens.length) % itens.length,
      Home: 0,
      End: itens.length - 1,
    };
    if (event.key in destinos) {
      event.preventDefault();
      itens[destinos[event.key]]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.fechar(true);
    } else if (event.key === 'Tab') {
      this.fechar();
    }
  }

  fecharAoClicarFora(event: Event): void {
    if (!event.composedPath().includes(this.host.nativeElement)) {
      this.fechar();
    }
  }

  private gatilho(): HTMLElement | null {
    return this.host.nativeElement.querySelector('vivest-icon-button');
  }

  private itens(): HTMLElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])'),
    );
  }
}
